import { appConfirm } from '../services/appDialogs';
import { AppSelect } from './ui/AppSelect';
import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import {
  UploadCloud, Loader2, FileText, Play, Eraser, Sparkles, XCircle,
  Grid, Video, BookOpen, Edit3, Settings, FileJson, Wand2, Film, LayoutGrid, Clapperboard, Crown, AlertTriangle, Scissors
} from 'lucide-react';
import mammoth from 'mammoth';
import { useToast } from '../context/ToastContext';
import { useTheme } from '../context/ThemeContext';
import { useUser } from '../context/UserContext';
import { API_HOST } from '../utils/constants';
import { authenticatedFetch } from '../utils/api';
import { useDelayedUnmount } from '../hooks/useDelayedUnmount';

const CoolUploader = ({ onSuccess, projectId, episodeId, hasExistingStoryboard }) => {
  const navigate = useNavigate();
  const { userId: currentUserId } = useUser();
  const { getTheme } = useTheme();
  const theme = getTheme();
  const [isUploading, setIsUploading] = useState(false);
  const [textContent, setTextContent] = useState('');
  const [loadingStage, setLoadingStage] = useState(''); // 新增：当前加载阶段
  const [scriptEpisodes, setScriptEpisodes] = useState([]);
  const [selectedScriptEpisodeId, setSelectedScriptEpisodeId] = useState('');
  const [isLoadingScriptEpisodes, setIsLoadingScriptEpisodes] = useState(false);

  // 分镜模式状态
  const [splitMode, setSplitMode] = useState("default");
  // 自定义提示词状态
  const [customPrompt, setCustomPrompt] = useState("");
  // 是否显示预览
  const [showPreview, setShowPreview] = useState(false);
  const toast = useToast();
  const customPromptSec = useDelayedUnmount(splitMode === 'custom', 150);
  const previewSec = useDelayedUnmount(showPreview, 150);

  const fetchScriptEpisodes = useCallback(async () => {
    if (!projectId) {
      setScriptEpisodes([]);
      setSelectedScriptEpisodeId('');
      return;
    }

    setIsLoadingScriptEpisodes(true);
    try {
      const data = await authenticatedFetch(`${API_HOST}/api/project/episode/list?projectId=${projectId}`);
      const episodesWithScript = Array.isArray(data)
        ? data.filter((ep) => ep?.scriptContent && ep.scriptContent.trim().length > 0)
        : [];
      setScriptEpisodes(episodesWithScript);
    } catch (error) {
      console.warn('[CoolUploader] 加载剧本中心集数失败', error);
    } finally {
      setIsLoadingScriptEpisodes(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchScriptEpisodes();
  }, [fetchScriptEpisodes]);

  useEffect(() => {
    if (!episodeId || selectedScriptEpisodeId || textContent.trim()) return;
    const currentEpisode = scriptEpisodes.find((ep) => String(ep.id) === String(episodeId));
    if (!currentEpisode?.scriptContent) return;
    setTextContent(currentEpisode.scriptContent);
    setSelectedScriptEpisodeId(String(currentEpisode.id));
  }, [episodeId, scriptEpisodes, selectedScriptEpisodeId, textContent]);

  // 模式配置（包含预览信息）
  const modeConfigs = {
    'default': {
      icon: <FileText size={18}/>,
      title: "标准剧本",
      desc: "智能提取场景、角色、光影等专业元素，自然流畅的分镜拆解",
      preview: {
        title: "标准剧本模式 (影视工业级)",
        features: ["格式智能解析", "长镜头动作流", "首帧锚点法", "防画风污染"],
        example: "△ 卧室光线昏暗，窗帘未拉严，微弱的月光从窗外洒进来。\n\n李平（声音沙哑，浑身发抖）：你们在干什么？！"
      }
    },
    'main-shot-director': {
      icon: <Clapperboard size={18}/>,
      title: "主镜导演法",
      desc: "先确定最少量主镜，再补必要连接镜，让叙事紧凑、镜头连续",
      preview: {
        title: "主镜导演法",
        features: ["戏点优先", "最少量主镜", "必要连接镜", "紧凑连续叙事"],
        example: "先锁定推动剧情的主镜与必要空间关系\n→ 只补连接主镜、补全信息、强调重点的镜头\n→ 避免平均切分和无效碎镜"
      }
    },
    'ultimate': {
      icon: <Crown size={18}/>,
      title: "全维度视听分镜",
      desc: "顶级视听编导系统，物理避障+叙事呼吸+动作四段式+自动资产",
      preview: {
        title: "顶级全维度视听分镜系统",
        features: ["空间站位死锁", "AI物理避障", "叙事呼吸感", "动作四段式拆解"],
        example: "铁律1: 空间站位+180度轴线锁定\n铁律2: 三拍子节奏+幕间气口缓冲\n铁律3: 蓄能/释放光影动力学\n铁律4: 起势→局部→结果→余波\n→ 自动提取角色(主角/反派造型区分)+场景+道具"
      }
    },
    'ultimate-assets': {
      icon: <Crown size={18}/>,
      title: "全维度+3D服化道",
      desc: "顶级视听分镜+3D国漫级服化道Agent，玄机/幻维级定妆建模",
      preview: {
        title: "全维度视听分镜+3D国漫服化道",
        features: ["四大铁律视听分镜", "3D国漫定妆建模", "SSS材质/PBR贴图", "武器解绑/标准站姿"],
        example: "铁律1-4: 全维度视听分镜系统\n→ 导演审核通过后\n→ 服化道Agent: 玄机/幻维级3D国漫定妆\n→ SSS材质皮肤/PBR服饰/武器独立提取\n→ 确认后生成资产图片"
      }
    },
    'novel': {
      icon: <BookOpen size={18}/>,
      title: "小说直转",
      desc: "纯旁白配画模式，原文一字不改，适合动态漫和有声书",
      preview: {
        title: "小说直转模式 (有声书/动态漫专用)",
        features: ["原文绝对零改动", "音画时间轴强制同步", "纯净自然光影(防止人脸崩坏)"],
        example: "这天夜里，狂风大作。李逍遥推开破庙的木门，看到神像前已经生起了一堆篝火。"
      }
    },
    'seedance': {
      icon: <Clapperboard size={18}/>,
      title: "视频生成",
      desc: "专为视频大模型优化，支持高级运镜与原生@资产引用",
      preview: {
        title: "视频 2.0 专用模式",
        features: ["连贯长镜头", "@资产原生语法", "复杂物理动态流", "文生视频直出"],
        example: "电影级写实风格。@徐宁 猛地向下拉动门把，沉重的木门被推开，门外的风雪瞬间灌入，吹得他的衣角剧烈翻飞。镜头跟随他缓慢向前推进。"
      }
    },
    'explainer': {
      icon: <Video size={18}/>,
      title: "解说视频",
      desc: "旁白进Content，画面作为B-Roll",
      preview: {
        title: "解说视频模式",
        features: ["旁白为主", "画面辅助", "适合教程/纪录片"],
        example: "镜头1: 特写\n旁白: 今天我们来讲解如何制作咖啡\n画面: 咖啡豆特写镜头..."
      }
    },
    'explainer-comic': {
      icon: <Film size={18}/>,
      title: "解说漫剧本",
      desc: "已拆好画面的解说剧本，解说词原封不动，AI补充分镜",
      preview: {
        title: "解说漫剧本模式",
        features: ["解说词原封不动", "画面已预拆分", "AI补充分镜信息"],
        example: "解说词：我猛地睁眼，发现自己身处刑台...\n画面1：刑台被阳光笼罩\n画面2：围观百姓挤作一团\n→ AI补充景别/光影/prompt"
      }
    },
    'grid': {
      icon: <Grid size={18}/>,
      title: "连续分镜",
      desc: "生成 2x2 网格图，展示连贯动作",
      preview: {
        title: "连续分镜模式",
        features: ["2x2网格布局", "展示连续动作", "适合动作场景"],
        example: "网格1: 起跳\n网格2: 空中翻转\n网格3: 落地准备\n网格4: 完美着陆"
      }
    },
    'with-assets': {
      icon: <Wand2 size={18}/>,
      title: "剧本+资产",
      desc: "AI拆分镜+自动提取角色/场景/道具并生图",
      preview: {
        title: "剧本+资产模式",
        features: ["自动提取角色", "生成场景图", "创建道具库"],
        example: "分镜 + 角色库(小明、小红) + 场景库(咖啡厅、公园) + 道具库(咖啡杯、手机)"
      }
    },
    'with-first-last-frame': {
      icon: <Film size={18}/>,
      title: "剧本+资产+首尾帧",
      desc: "细粒度分镜，每个分镜生成首尾帧，支持连续视频生成",
      preview: {
        title: "首尾帧模式",
        features: ["生成首帧和尾帧", "支持视频生成", "最精细的分镜"],
        example: "镜头1:\n首帧: 小明站立\n尾帧: 小明坐下\n→ 可生成连续视频"
      }
    },
    'nine-grid': {
      icon: <LayoutGrid size={18}/>,
      title: "9宫格剧情法",
      desc: "压缩为9个关键节点，生成提示词用于画布模式",
      preview: {
        title: "9宫格剧情法",
        features: ["9个关键节点", "生成提示词节点", "画布模式操作", "按需扩写4宫格"],
        example: "输出: 1个9宫格提示词 + 若干4宫格提示词\n→ 在画布模式中选择提示词生成图片"
      }
    },
    'two-stage': {
      icon: <LayoutGrid size={18}/>,
      title: "两段式剧本拆分",
      desc: "Stage 1: 生成关键帧分镜 → Stage 2: 画布中扩展为9宫格",
      preview: {
        title: "两段式剧本拆分 (推荐)",
        features: ["关键帧分镜", "画布9宫格扩展", "资产引用提取", "批量生图"],
        example: "Stage 1: 生成10-20个关键帧分镜\n→ 点击\"扩展到画布\"按钮\n→ Stage 2: 每个关键帧自动扩展为9宫格\n→ 在画布中批量生图"
      }
    },
    'explainer-comic-assets': {
      icon: <Film size={18}/>,
      title: "解说漫+资产",
      desc: "解说词+画面格式，自动提取角色/场景/道具并生图",
      preview: {
        title: "解说漫+资产模式",
        features: ["解说词原封不动", "画面已预拆分", "自动提取角色/场景/道具", "生成资产图片"],
        example: "人物形象：秀兰、王大脚...\n\n解说词：xxx\n画面1：xxx\n画面2：xxx\n→ 提取角色+场景+道具+生成分镜"
      }
    },
    'custom': {
      icon: <Edit3 size={18}/>,
      title: "自定义",
      desc: "完全使用您编写的 Prompt",
      preview: {
        title: "自定义模式",
        features: ["完全自定义", "使用自己的Prompt", "最大灵活性"],
        example: "使用您自己编写的System Prompt来控制AI的分镜风格和输出格式"
      }
    },
    'import-json': {
      icon: <FileJson size={18}/>,
      title: "导入JSON",
      desc: "从旧服务器导入分镜数据",
      preview: {
        title: "导入JSON模式",
        features: ["导入现有数据", "无需AI生成", "快速迁移"],
        example: "直接导入已有的分镜JSON数据，无需重新生成"
      }
    }
  };

  // 处理文件读取 (保留您原有的 mammoth 逻辑)
  const readFileContent = async (file) => {
    setIsUploading(true);
    try {
      let result = "";
      if (file.name.endsWith('.docx')) {
        const reader = new FileReader();
        result = await new Promise((resolve, reject) => {
          reader.onload = async (e) => {
            try {
              const arrayBuffer = e.target.result;
              const mammothResult = await mammoth.extractRawText({ arrayBuffer });
              resolve(mammothResult.value);
            } catch (err) { reject(err); }
          };
          reader.onerror = reject;
          reader.readAsArrayBuffer(file);
        });
      } else {
        const reader = new FileReader();
        result = await new Promise((resolve, reject) => {
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = reject;
          reader.readAsText(file);
        });
      }
      setTextContent(result);
    } catch (error) {
      console.error("文件读取失败", error);
      toast.error("文件解析失败，请重试");
    } finally {
      setIsUploading(false);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      readFileContent(file);
    }
  }, []);

  const handleImportEpisodeScript = async (value) => {
    if (!value) {
      setSelectedScriptEpisodeId('');
      return;
    }

    const episode = scriptEpisodes.find((ep) => String(ep.id) === String(value));
    if (!episode?.scriptContent) {
      toast.warning('该集没有可导入的剧本内容');
      return;
    }

    if (textContent.trim() && textContent !== episode.scriptContent) {
      const confirmed = (await appConfirm('当前输入框已有内容，是否用剧本中心选中的集数覆盖？'));
      if (!confirmed) return;
    }

    setTextContent(episode.scriptContent);
    setSelectedScriptEpisodeId(value);
    toast.success(`已导入${episode.title || `第${episode.sortOrder || ''}集`}剧本`);
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    disabled: isUploading,
    noClick: true,
    accept: {
      'text/plain': ['.txt'],
      'text/markdown': ['.md'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    }
  });

  // ✅ 修改：提交逻辑，携带 mode 和 customPrompt
  const handleGenerate = async () => {
    // 检查是否有现有分镜，给出警告
    if (hasExistingStoryboard && splitMode !== "import-json") {
      const confirmed = (await appConfirm(
        "⚠️ 当前已有分镜数据\n\n重新导入剧本将会覆盖现有的分镜表，此操作不可撤销。\n\n确定要继续吗？"
      ));
      if (!confirmed) {
        return;
      }
    }

    // 导入 JSON 模式特殊处理
    if (splitMode === "import-json") {
      // 这里需要用户上传 JSON 文件，但 UI 上传区目前只支持文本
      // 简单起见，提示用户在输入框粘贴 JSON 内容
      if (!textContent || textContent.trim().length === 0) {
        toast.warning("请导入 JSON 文件或将 JSON 内容粘贴到输入框");
        return;
      }
      await handleJsonImportFromText(textContent);
      return;
    }

    if (!textContent || textContent.trim().length === 0) {
      toast.warning("请输入或导入剧本内容");
      return;
    }

    if (splitMode === "custom" && !customPrompt.trim()) {
      toast.warning("自定义模式下，请填写 Prompt");
      return;
    }

    setIsUploading(true);
    setLoadingStage('scriptwriter'); // 开始编剧阶段
    try {
      // 传递一个包装函数，用于同时更新 loadingStage 和 isUploading
      const wrappedSetLoadingStage = (stage) => {
        setLoadingStage(stage);
        // 当 stage 为空字符串时，表示完成或失败，重置 isUploading
        if (stage === '') {
          setIsUploading(false);
        } else {
          // 非空 stage 时，确保 isUploading 为 true（支持重新激活）
          setIsUploading(true);
        }
      };
      await onSuccess(textContent, splitMode, customPrompt, wrappedSetLoadingStage);
    } catch (error) {
      console.error(error);
      toast.error("生成失败: " + (error.message || "请重试"));
      setIsUploading(false);
      setLoadingStage('');
    }
  };

  // 从文本内容处理 JSON 导入
  const handleJsonImportFromText = async (jsonContent) => {
    setIsUploading(true);
    setLoadingStage('finalizing');
    try {
      // 解析 JSON
      let parsedShots;
      try {
        parsedShots = JSON.parse(jsonContent);
      } catch {
        throw new Error("JSON 格式错误，请检查内容");
      }

      // 构建请求体，携带当前用户和集数信息
      const requestBody = {
        shots: parsedShots,
        userId: currentUserId,
        episodeId: episodeId
      };

      await authenticatedFetch(`${API_HOST}/api/admin/import-storyboard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      toast.success("分镜数据导入成功");
      onSuccess(jsonContent, 'import', '', () => {}); // 传递空函数作为 setLoadingStage

    } catch (error) {
      console.error("JSON 导入失败", error);
      toast.error("导入失败: " + (error.message || "请重试"));
    } finally {
      setIsUploading(false);
      setLoadingStage('');
    }
  };

  return (
    <div className="w-full max-w-6xl mx-auto flex flex-col gap-8 animate-in fade-in slide-in-from-bottom-4 duration-500 p-4">
      
      {/* 头部引导 */}
      <div className="text-center space-y-2">
        <h2 className="text-4xl font-bold inline-flex items-center gap-3" style={{ color: 'var(--color-text-primary)' }}>
          <Sparkles size={32} style={{ color: theme.colors.accentPrimary }} /> 分镜拆解
        </h2>
        <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
          优先从剧本中心选择已保存的本集剧本，再生成分镜表
        </p>
      </div>

      <div
        className="rounded-2xl border p-4 flex flex-wrap items-center justify-between gap-3"
        style={{
          backgroundColor: scriptEpisodes.length > 0 ? 'var(--color-bg-secondary)' : 'rgba(212,167,68,0.12)',
          borderColor: scriptEpisodes.length > 0 ? 'var(--color-border-primary)' : 'var(--color-warning)',
        }}
      >
        <div className="flex min-w-0 items-start gap-3">
          <div className="mt-0.5 h-9 w-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'var(--color-accent-light)', color: theme.colors.accentPrimary }}>
            {scriptEpisodes.length > 0 ? <BookOpen size={17} /> : <AlertTriangle size={17} />}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>
              {scriptEpisodes.length > 0 ? `剧本中心已有 ${scriptEpisodes.length} 集可用剧本` : '当前项目还没有可用的单集剧本'}
            </div>
            <div className="mt-1 text-xs leading-5" style={{ color: 'var(--color-text-muted)' }}>
              分镜、大故事板和小故事板都应该以正式单集剧本为地基。临时粘贴也能生成，但后续追溯、重排和一致性会弱很多。
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => navigate(`/project/${projectId}/script-analysis`)}
            className="h-9 px-3 rounded-lg border text-xs font-bold inline-flex items-center gap-2 cursor-pointer"
            style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-card)', color: 'var(--color-text-primary)' }}
          >
            <Scissors size={14} />
            去剧本中心拆剧本
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">

        {/* 左侧：上传 + 输入区 (占 3 列) */}
        <div className="lg:col-span-3">
            <div
                {...getRootProps()}
                className={`
                relative group overflow-hidden rounded-[2rem] border-2 shadow-2xl h-[600px] flex flex-col
                transition-all duration-300
                `}
                style={{
                    borderColor: isDragActive ? theme.colors.accentPrimary : 'var(--color-border-primary)',
                    backgroundColor: isDragActive ? 'var(--color-accent-light)' : 'var(--color-bg-secondary)',
                }}
            >
                <input {...getInputProps()} />

                {isDragActive && (
                <div className="absolute inset-0 z-50 flex items-center justify-center backdrop-blur-sm" style={{ backgroundColor: 'var(--overlay-medium)' }}>
                    <div className="flex flex-col items-center" style={{ color: theme.colors.accentPrimary }}>
                        <UploadCloud size={48} />
                        <p className="font-bold mt-2 text-lg">释放文件以导入内容</p>
                    </div>
                </div>
                )}

                {isUploading && (
                <div className="absolute inset-0 z-50 flex items-center justify-center backdrop-blur-md" style={{ backgroundColor: 'var(--overlay-heavy)' }}>
                    <div className="flex flex-col items-center gap-4">
                        {loadingStage.startsWith('error:') ? (
                          <>
                            <XCircle size={48} style={{ color: 'var(--color-error)' }} />
                            <div className="text-center space-y-2 max-w-md px-4">
                              <p className="text-lg font-bold" style={{ color: 'var(--color-error)' }}>生成失败</p>
                              <p className="text-sm leading-relaxed break-all" style={{ color: 'var(--color-text-secondary)', maxHeight: '200px', overflowY: 'auto' }}>
                                {loadingStage.slice(6)}
                              </p>
                              <button
                                onClick={() => { setLoadingStage(''); setIsUploading(false); }}
                                className="mt-3 px-6 py-2 rounded-xl text-sm font-bold transition-all duration-200 cursor-pointer"
                                style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border-primary)' }}
                              >
                                关闭
                              </button>
                            </div>
                          </>
                        ) : (
                          <>
                            <Loader2 size={40} className="animate-spin" style={{ color: theme.colors.accentPrimary }} />
                            <div className="text-center space-y-1">
                                <p className="text-lg font-bold" style={{ color: 'var(--color-text-primary)' }}>
                                  {loadingStage === 'scriptwriter' && (splitMode === 'explainer-comic' ? '正在解析预拆分剧本...' : '编剧正在拆解剧情...')}
                                  {loadingStage === 'storyboarder' && '分镜师正在思考分镜...'}
                                  {loadingStage === 'director' && '导演正在审查错误...'}
                                  {loadingStage === 'costume_designer' && '服化道正在设计角色造型...'}
                                  {loadingStage === 'finalizing' && '一切就绪，分镜马上揭晓！'}
                                  {loadingStage.startsWith('stage:') && `处理中：${loadingStage.slice(6)}`}

                                  {!loadingStage && '正在解析与拆分...'}
                                </p>
                                <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                                  {loadingStage === 'scriptwriter' && (splitMode === 'explainer-comic' ? '解析解说词和画面，跳过编剧直接进入分镜师' : '提取人物、场景、道具，结构化剧本')}
                                  {loadingStage === 'storyboarder' && '设计景别、角度、运镜，生成专业分镜'}
                                  {loadingStage === 'director' && '检查动静分离、资产引用、连戏性'}
                                  {loadingStage === 'costume_designer' && '3D国漫级定妆建模，提取角色/场景/道具'}
                                  {loadingStage === 'finalizing' && '保存分镜数据，准备展示'}
                                  {loadingStage.startsWith('stage:') && '任务仍在进行中，请勿关闭页面'}
                                  {!loadingStage && '正在分析剧情逻辑'}
                                </p>
                            </div>
                          </>
                        )}
                    </div>
                </div>
                )}

                <div className="flex-1 p-8 flex flex-col relative">
                    {!textContent && (
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none select-none" style={{ color: 'var(--color-text-muted)' }}>
                            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 border" style={{ backgroundColor: 'var(--color-bg-tertiary)', borderColor: 'var(--color-border-primary)' }}>
                                <FileText size={32} />
                            </div>
                            <p className="text-lg font-bold mb-2">在此处粘贴剧本...</p>
                            <p className="text-sm" style={{ opacity: 0.7 }}>支持 .docx / .txt / .md 拖拽上传</p>
                        </div>
                    )}
                    <textarea
                        value={textContent}
                        onChange={(e) => setTextContent(e.target.value)}
                        className="w-full h-full flex-1 bg-transparent border-none outline-none text-lg font-mono leading-relaxed resize-none custom-scrollbar z-10 placeholder-transparent"
                        style={{ color: 'var(--color-text-primary)' }}
                        placeholder="在此粘贴..."
                        spellCheck={false}
                    />
                </div>

                <div className="h-20 border-t px-6 flex items-center justify-between" style={{ backgroundColor: 'var(--color-bg-tertiary)', borderColor: 'var(--color-border-primary)' }}>
                    <div className="flex items-center gap-3 min-w-0">
                        <button
                            onClick={open}
                            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-colors duration-200 border cursor-pointer bg-[var(--color-bg-primary)] border-[var(--color-border-primary)] text-[var(--color-text-secondary)] hover:border-[var(--color-border-hover)] hover:text-[var(--color-text-primary)]"
                        >
                            <UploadCloud size={16} />
                            <span>导入</span>
                        </button>
                        <div
                            className="flex items-center gap-2 h-10 px-3 rounded-xl border min-w-0 max-w-[260px]"
                            style={{
                                backgroundColor: 'var(--color-bg-primary)',
                                borderColor: 'var(--color-border-primary)',
                                color: 'var(--color-text-secondary)'
                            }}
                            title="从剧本中心选择某一集导入到输入框"
                        >
                            <BookOpen size={16} style={{ color: theme.colors.accentPrimary }} />
                            <AppSelect
                                value={selectedScriptEpisodeId}
                                onChange={(e) => handleImportEpisodeScript(e.target.value)}
                                onFocus={fetchScriptEpisodes}
                                disabled={isUploading}
                                className="h-full min-w-0 max-w-[180px] bg-transparent outline-none text-sm font-bold cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
                                style={{ color: 'var(--color-text-secondary)' }}
                            >
                                <option value="" style={{ backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-primary)' }}>
	                                    {isLoadingScriptEpisodes ? '加载剧本...' : scriptEpisodes.length > 0 ? '选择本集剧本' : '无集数剧本'}
                                </option>
                                {scriptEpisodes.map((ep) => (
                                    <option
                                        key={ep.id}
                                        value={ep.id}
                                        style={{ backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-primary)' }}
                                    >
                                        {ep.sortOrder ? `第${ep.sortOrder}集 · ` : ''}{ep.title || '未命名'} ({ep.scriptContent.length}字)
                                    </option>
                                ))}
                            </AppSelect>
                        </div>
                        {textContent && (
                            <button
                                onClick={() => setTextContent('')}
                                className="p-2 rounded-xl transition-colors duration-200 cursor-pointer text-[var(--color-text-muted)] hover:bg-[var(--color-bg-tertiary)] hover:text-[var(--color-text-primary)]"
                                title="清空"
                            >
                                <Eraser size={18} />
                            </button>
                        )}
                    </div>
                    
                    {/* 这里的字数统计或提示 */}
                    <div className="text-xs font-mono" style={{ color: 'var(--color-text-muted)' }}>
                         {textContent ? `${textContent.length} 字符` : "等待输入"}
                    </div>
                </div>
            </div>
        </div>

        {/* 右侧：模式选择 + 预览 (占 2 列) */}
        <div className="lg:col-span-2 flex flex-col gap-4 h-[600px]">
            {/* 模式选择区域 */}
            <div className="flex-1 border-2 rounded-[2rem] p-6 flex flex-col shadow-xl overflow-hidden" style={{ backgroundColor: 'var(--color-bg-secondary)', borderColor: 'var(--color-border-primary)' }}>
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2" style={{ color: theme.colors.accentPrimary }}>
                        <Settings size={20} />
                        <h3 className="font-bold text-lg">拆分模式</h3>
                    </div>
                    <button
                        onClick={() => setShowPreview(!showPreview)}
                        className={`text-xs px-3 py-1.5 rounded-lg border transition-colors duration-200 cursor-pointer ${!showPreview ? 'hover:bg-[var(--color-accent-light)]' : ''}`}
                        style={{
                            backgroundColor: showPreview ? theme.colors.accentPrimary : undefined,
                            borderColor: theme.colors.accentPrimary,
                            color: showPreview ? 'white' : theme.colors.accentPrimary
                        }}
                    >
                        {showPreview ? '隐藏预览' : '显示预览'}
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto custom-scrollbar space-y-2.5 pr-2">
                    {Object.entries(modeConfigs).map(([key, config]) => (
                        <ModeOption
                            key={key}
                            active={splitMode === key}
                            onClick={() => setSplitMode(key)}
                            icon={config.icon}
                            title={config.title}
                            desc={config.desc}
                            theme={theme}
                        />
                    ))}
                </div>

                {customPromptSec.mounted && (
                    <div className={`mt-4 ${customPromptSec.exiting ? 'animate-out fade-out slide-out-to-right-4 duration-150' : 'animate-in fade-in slide-in-from-right-4 duration-200'}`}>
                        <label className="text-xs mb-2 block font-bold" style={{ color: 'var(--color-text-muted)' }}>SYSTEM PROMPT</label>
                        <textarea
                            value={customPrompt}
                            onChange={(e) => setCustomPrompt(e.target.value)}
                            className="w-full h-24 rounded-xl p-3 text-xs outline-none resize-none custom-scrollbar"
                            style={{
                                backgroundColor: 'var(--color-bg-overlay)',
                                border: '1px solid var(--color-border-primary)',
                                color: 'var(--color-text-primary)'
                            }}
                            placeholder="Role: Director..."
                        />
                    </div>
                )}

                <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--color-border-primary)' }}>
                    <button
                        onClick={handleGenerate}
                        disabled={!textContent || isUploading}
                        className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-bold text-base transition-all duration-200 shadow-xl"
                        style={{
                            backgroundColor: (!textContent || isUploading) ? 'var(--color-bg-primary)' : theme.colors.accentPrimary,
                            color: (!textContent || isUploading) ? 'var(--color-text-muted)' : 'white',
                            cursor: (!textContent || isUploading) ? 'not-allowed' : 'pointer',
                            opacity: (!textContent || isUploading) ? 0.5 : 1,
                            border: '1px solid var(--color-border-primary)'
                        }}
                    >
                        {isUploading ? <Loader2 size={20} className="animate-spin"/> : <Play size={20} fill="currentColor" />}
                        <span>开始生成</span>
                    </button>
                </div>
            </div>

            {/* 预览区域 */}
            {previewSec.mounted && (
                <div className={`border-2 rounded-[2rem] p-6 shadow-xl ${previewSec.exiting ? 'animate-out fade-out slide-out-to-bottom-2 duration-150' : 'animate-in fade-in slide-in-from-bottom-4 duration-300'}`} style={{ backgroundColor: 'var(--color-bg-secondary)', borderColor: 'var(--color-border-primary)', maxHeight: '300px' }}>
                    <div className="flex items-center gap-2 mb-4" style={{ color: theme.colors.accentPrimary }}>
                        <Sparkles size={18} />
                        <h4 className="font-bold text-sm">{modeConfigs[splitMode].preview.title}</h4>
                    </div>

                    <div className="space-y-3">
                        <div>
                            <p className="text-xs font-bold mb-2" style={{ color: 'var(--color-text-muted)' }}>特性</p>
                            <div className="flex flex-wrap gap-2">
                                {modeConfigs[splitMode].preview.features.map((feature, idx) => (
                                    <span
                                        key={idx}
                                        className="text-xs px-2.5 py-1 rounded-lg"
                                        style={{
                                            backgroundColor: 'var(--color-bg-tertiary)',
                                            color: 'var(--color-text-secondary)',
                                            border: '1px solid var(--color-border-primary)'
                                        }}
                                    >
                                        {feature}
                                    </span>
                                ))}
                            </div>
                        </div>

                        <div>
                            <p className="text-xs font-bold mb-2" style={{ color: 'var(--color-text-muted)' }}>示例输出</p>
                            <div
                                className="text-xs p-3 rounded-lg font-mono leading-relaxed overflow-y-auto custom-scrollbar"
                                style={{
                                    backgroundColor: 'var(--color-bg-overlay)',
                                    color: 'var(--color-text-secondary)',
                                    border: '1px solid var(--color-border-primary)',
                                    maxHeight: '120px'
                                }}
                            >
                                {modeConfigs[splitMode].preview.example}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>

      </div>
    </div>
  );
};

// 辅助组件：模式选项按钮 (保持整体风格一致)
const ModeOption = React.memo(({ active, onClick, icon, title, desc, theme }) => (
    <button
        onClick={onClick}
        className={`w-full text-left p-3 rounded-xl border-2 transition-colors duration-200 flex items-start gap-3 group cursor-pointer ${active ? '' : 'border-[var(--color-border-primary)] hover:border-[var(--color-border-hover)] hover:bg-[var(--color-bg-tertiary)]'}`}
        style={active ? {
            backgroundColor: 'var(--color-accent-light)',
            borderColor: theme.colors.accentPrimary,
        } : undefined}
    >
        <div
            className="mt-0.5 p-1.5 rounded-lg flex-shrink-0 transition-colors duration-200"
            style={{
                backgroundColor: 'var(--color-bg-tertiary)',
                color: active ? theme.colors.accentPrimary : 'var(--color-text-muted)'
            }}
        >
            {icon}
        </div>
        <div className="flex-1 min-w-0">
            <div className="text-sm font-bold truncate" style={{ color: active ? 'var(--color-text-primary)' : 'var(--color-text-secondary)' }}>
                {title}
            </div>
            <div className="text-xs leading-tight mt-0.5" style={{ color: active ? 'var(--color-text-secondary)' : 'var(--color-text-muted)', opacity: active ? 1 : 0.7 }}>
                {desc}
            </div>
        </div>
    </button>
));
ModeOption.displayName = 'ModeOption';

export default React.memo(CoolUploader);
